/**
 * État de l'application : une source unique, des abonnés notifiés à chaque
 * changement.
 *
 * Les modules d'UI ne se parlent jamais entre eux et ne se lisent pas
 * mutuellement : ils écrivent dans l'état et réagissent à ses notifications.
 * C'est ce qui permet au temps réel de Supabase d'emprunter exactement le même
 * chemin qu'un clic local — un dossier modifié par un collègue passe par le
 * même setRecord() et redessine les mêmes vues.
 */

import { ALL_TYPES } from './domain/filters.js'
import { DEFAULT_SETTINGS, DEFAULT_STATUS_FILTERS } from './domain/constants.js'
import { recordKey } from './domain/record.js'

// v2 : le défaut est passé de « tous les statuts » à « prospects seuls ». Le
// numéro est incrémenté à dessein — sans lui, les postes ayant déjà utilisé
// l'outil garderaient leur ancien état enregistré et ne verraient jamais le
// nouveau défaut.
const LS_FILTERS = 'synchro_sales_filters_v2'

/**
 * Les filtres restent locaux : ils relèvent du confort de lecture de chacun,
 * pas de la donnée partagée. Les synchroniser ferait bouger la carte d'une
 * personne parce qu'une autre a cliqué ailleurs.
 */
function loadFilters() {
  try {
    const saved = JSON.parse(localStorage.getItem(LS_FILTERS))
    return {
      status: { ...DEFAULT_STATUS_FILTERS, ...(saved?.status || {}) },
      type: { ...ALL_TYPES, ...(saved?.type || {}) },
    }
  } catch {
    return { status: { ...DEFAULT_STATUS_FILTERS }, type: { ...ALL_TYPES } }
  }
}

function saveFilters(state) {
  try {
    localStorage.setItem(LS_FILTERS, JSON.stringify({ status: state.statusFilters, type: state.typeFilters }))
  } catch {
    // Navigation privée, quota plein : perdre une préférence d'affichage
    // ne justifie pas d'interrompre l'utilisateur.
  }
}

const saved = loadFilters()

const state = {
  user: null,
  establishments: [], // statiques, chargées une fois
  records: new Map(), // recordKey -> dossier
  notes: new Map(), // recordKey -> note[]
  settings: { ...DEFAULT_SETTINGS },
  statusFilters: saved.status,
  typeFilters: saved.type,
  selection: null, // {kind, id} — un établissement de la carte
  /**
   * Départements dépliés, par identifiant. Plusieurs à la fois, à dessein.
   *
   * N'en garder qu'un ouvert obligeait à refermer le précédent : quand il se
   * trouvait au-dessus, sa hauteur quittait le flux et la ligne qu'on venait
   * de cliquer remontait de ~234 px, s'échappant sous le curseur. Aucune
   * compensation ne rattrape ça — le panneau n'a pas toujours de quoi défiler.
   * Ne rien refermer est la seule façon que rien ne bouge.
   */
  openDepts: new Set(),
  // La colonne de résultats ne s'ouvre que sur demande explicite. Elle s'ouvrait
  // autrefois dès qu'un filtre était actif, ce qui, depuis que « prospects
  // seuls » est le défaut, la faisait apparaître au démarrage avec 810 lignes
  // que personne n'avait demandées.
  listOpen: false,
  loading: true,
  error: null,
}

const listeners = new Set()

/** @returns {() => void} pour se désabonner */
export function subscribe(listener) {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

/**
 * `changed` nomme ce qui a bougé, pour que chaque vue redessine seulement ce
 * qui la concerne : réafficher 705 marqueurs parce qu'un champ de saisie a
 * changé serait perceptible.
 */
function emit(changed) {
  for (const listener of listeners) listener(state, changed)
}

export function getState() {
  return state
}

export function getRecord(kind, id) {
  return state.records.get(recordKey(kind, id))
}

export function getNotes(kind, id) {
  return state.notes.get(recordKey(kind, id)) || []
}

export function getSelected() {
  if (!state.selection) return null
  const { kind, id } = state.selection
  return state.establishments.find((e) => e.kind === kind && e.id === id) || null
}

// --- Mutations : seule façon d'écrire dans l'état ---------------------------

export function setUser(user) {
  state.user = user
  emit(['user'])
}

export function setEstablishments(list) {
  state.establishments = list
  emit(['establishments'])
}

export function setData({ records, notes, settings }) {
  state.records = records
  state.notes = notes
  state.settings = settings
  state.loading = false
  state.error = null
  emit(['records', 'notes', 'settings', 'loading'])
}

export function setRecord(record) {
  state.records.set(recordKey(record.kind, record.id), record)
  emit(['records'])
}

export function removeRecord(kind, id) {
  const key = recordKey(kind, id)
  if (!state.records.delete(key)) return
  // Les notes d'un dossier supprimé n'ont plus de rattachement : le déclencheur
  // records_cascade_notes les efface en base, on fait de même en mémoire pour
  // ne pas attendre un rechargement.
  state.notes.delete(key)
  emit(['records', 'notes'])
}

export function setSettings(settings) {
  state.settings = settings
  emit(['settings'])
}

export function addNote(note) {
  const key = recordKey(note.kind, note.establishmentId)
  const list = state.notes.get(key) || []
  // Le temps réel renvoie aussi ses propres insertions : sans ce garde, une
  // note écrite ici apparaîtrait deux fois.
  if (list.some((n) => n.id === note.id)) return
  state.notes.set(key, [note, ...list])
  emit(['notes', 'records'])
}

export function removeNote(noteId) {
  for (const [key, list] of state.notes) {
    const next = list.filter((n) => n.id !== noteId)
    if (next.length !== list.length) {
      state.notes.set(key, next)
      emit(['notes', 'records'])
      return
    }
  }
}

export function setStatusFilters(filters) {
  state.statusFilters = filters
  saveFilters(state)
  emit(['statusFilters'])
}

export function setTypeFilters(filters) {
  state.typeFilters = filters
  saveFilters(state)
  emit(['typeFilters'])
}

export function setListOpen(open) {
  if (state.listOpen === open) return
  state.listOpen = open
  emit(['listOpen'])
}

export function select(kind, id) {
  state.selection = { kind, id }
  // Changer d'établissement referme les départements dépliés : ils
  // appartenaient à l'hôpital précédent.
  state.openDepts.clear()
  emit(['selection', 'openDepts'])
}

export function clearSelection() {
  if (!state.selection) return
  state.selection = null
  state.openDepts.clear()
  emit(['selection', 'openDepts'])
}

/** Déplie ou referme un département, sans toucher aux autres. */
export function toggleDept(deptId) {
  if (!state.openDepts.delete(deptId)) state.openDepts.add(deptId)
  emit(['openDepts'])
}

export function isDeptOpen(deptId) {
  return state.openDepts.has(deptId)
}

export function setError(error) {
  state.error = error
  state.loading = false
  emit(['error', 'loading'])
}
